import { Ban, CheckCircle, ClockFading, Timer, XCircle } from 'lucide-react';
import { Duration } from 'luxon';
import { memo, useEffect, useMemo, useState } from 'react';

import { useIntl } from '../../../hooks';
import { getSuppressionStatus, suppressionDuration } from '../../../services/suppressions';
import { type Suppression, SuppressionStatus } from '../../../types';
import { TableCell, TableRow } from '../../ui';

const suppressionCellClassName = 'text-right pl-8';

type SuppressionEventRowProps = {
  suppression: Suppression;
  colSpan: number;
};

export const SuppressionEventRow = memo(function SuppressionEventRow({
  suppression,
  colSpan,
}: SuppressionEventRowProps) {
  const intl = useIntl();

  const status = useMemo(() => getSuppressionStatus(suppression), [suppression]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status !== SuppressionStatus.IN_PROGRESS) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const durationLabel = useMemo(
    () =>
      suppression.startedAt
        ? (suppressionDuration(suppression) as Duration).toHuman({
            unitDisplay: 'short',
            showZeros: false,
          })
        : `${intl.formatMessage({ id: 'detectionStatus.suppressionInitiated' })}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suppression, intl, tick],
  );

  return (
    <TableRow className="bg-background-light">
      <TableCell></TableCell>
      <TableCell colSpan={colSpan} className={suppressionCellClassName}>
        <div className="flex items-center gap-2 pl-12 whitespace-nowrap">
          {status === SuppressionStatus.STOPPED && (
            <Ban
              className="h-4 w-4 text-destructive"
              aria-label={intl.formatMessage({ id: 'suppressionsTable.statusStopped' })}
            />
          )}
          {status === SuppressionStatus.INITIATED && (
            <ClockFading
              className="h-4 w-4 text-yellow"
              aria-label={intl.formatMessage({ id: 'detectionStatus.suppressionInitiated' })}
            />
          )}
          {status === SuppressionStatus.IN_PROGRESS && (
            <Timer
              className="h-4 w-4 text-yellow"
              aria-label={intl.formatMessage({ id: 'detectionStatus.suppressionInProgress' })}
            />
          )}
          {status === SuppressionStatus.SUCCESS && (
            <CheckCircle
              className="h-4 w-4 text-success"
              aria-label={intl.formatMessage({ id: 'detectionStatus.suppressionSuccess' })}
            />
          )}
          {status === SuppressionStatus.FAILED && (
            <XCircle
              className="h-4 w-4 text-destructive"
              aria-label={intl.formatMessage({ id: 'detectionStatus.suppressionFailed' })}
            />
          )}
          <span className="text-foreground">[{durationLabel}]</span>
          {suppression.reason && <span className="text-foreground">{suppression.reason}</span>}
        </div>
      </TableCell>
      <TableCell>
        {suppression.startedAt && (
          <>
            {suppression.startedAt.toFormat('HH:mm:ss')} →{' '}
            {suppression.finishedAt ? suppression.finishedAt.toFormat('HH:mm:ss') : '...'}
          </>
        )}
      </TableCell>
    </TableRow>
  );
});
